import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useFolders } from '@/api/folders';
import { useMoveFile } from '@/api/files';
import { useToast } from '@/hooks/useToast';
import { apiError } from '@/lib/api';
import { cn } from '@/lib/cn';

interface Props {
  open: boolean;
  onClose: () => void;
  file: { id: string; name: string; folder_id: string };
  workspaceId: string;
}

export function MoveFileModal({ open, onClose, file, workspaceId }: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const folders = useFolders(workspaceId);
  const move = useMoveFile();
  const toast = useToast();

  const handleSubmit = () => {
    if (!selectedId) return;
    move.mutate(
      { id: file.id, folder_id: selectedId, prev_folder_id: file.folder_id },
      {
        onSuccess: () => {
          toast.success('File moved');
          onClose();
        },
        onError: (e) => toast.error('Move failed', apiError(e)),
      },
    );
  };

  const available = (folders.data ?? []).filter((f) => f.id !== file.folder_id);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Move to folder"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={move.isPending}
            disabled={!selectedId}
          >
            Move
          </Button>
        </>
      }
    >
      <p className="text-xs text-slate-500 mb-2">
        Pick a destination folder in the same workspace. The file's history and comments stay with it.
      </p>
      <p className="text-sm text-slate-600 mb-3">
        Select a destination folder for <span className="font-medium">{file.name}</span>.
      </p>
      {folders.isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : available.length === 0 ? (
        <p className="text-sm text-slate-500">No other folders available.</p>
      ) : (
        <ul className="border border-bb-lilac divide-y divide-slate-100 max-h-60 overflow-y-auto custom-scrollbar">
          {available.map((folder) => (
            <li key={folder.id}>
              <button
                type="button"
                onClick={() => setSelectedId(folder.id)}
                className={cn(
                  'w-full text-left px-3 py-2 text-sm hover:bg-slate-50',
                  selectedId === folder.id
                    ? 'bg-bb-navy/5 text-bb-navy font-medium'
                    : 'text-slate-700',
                )}
              >
                {folder.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
